import axios from "axios";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "src/redux/reducers";
import { getProjectAudienceOverlap } from "src/redux";
import CustomCheckbox from "src/app/components/CustomCheckbox/CustomCheckbox";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { Img } from "src/app/components/Img/Img";
import Modal, {
  ModalScroller,
  ModalTitle,
  OutsideClickModes,
} from "src/app/components/Modal/Modal";
import { SocialProfile } from "src/app/modals/AddSocialProfilesModal/AddSocialProfilesModal";
import { Metadata } from "src/app/Task/Subtask/Subtask";
import { CoverImage, TaskType } from "src/types";
import { API_URLS } from "src/utils/API_URLS";
import { parseErrorMessages, showErrorToast } from "src/utils/methods";
import creatorPlaceholder from "src/images/creator-placeholder.svg";
import MemoizedLoader from "src/app/components/Loader/Loader";
import SocialProfileIcons from "src/app/components/IconsWithStatus/SocialProfileIcons/SocialProfileIcons";
import CustomSelect from "src/app/components/CustomSelect/CustomSelect";
import { ReactComponent as PublisherIcon } from "src/images/client-orange.svg";
import { ReactComponent as NonPublisherIcon } from "src/images/two-people-stroke.svg";
import { SelectOption } from "src/app/modals/ManageFieldModal/SocialMetrics";
import { getAOCreatorsSelectOptions } from "./utils";

import "./SelectCreatorsForAOModal.scss";

type CreatorForAO = {
  wsTaskUuid: string;
  title: string;
  rank: string;
  taskType: TaskType;
  metadata: Metadata[];
  cover: CoverImage;
  shared: false;
  wsGlobalTaskUuid: string;
  created: string;
  isPublisher: boolean;
  socialProfiles: SocialProfile[];
};

interface Props {
  onClose: () => void;
}

export const SelectCreatorsForAOModal = (props: Props) => {
  const { onClose } = props;

  const selectOptions = getAOCreatorsSelectOptions();

  const [allCreators, setAllCreators] = useState<CreatorForAO[]>([]);
  const [filteredCreators, setFilteredCreators] = useState<CreatorForAO[]>([]);
  const [selectedCreators, setSelectedCreators] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectValue, setSelectValue] = useState<SelectOption>(
    selectOptions[0],
  );
  const [analyzeLoading, setAnalyzeLoading] = useState(false);

  const { audienceOverlap, projectBasicData } = useSelector(
    (state: RootState) => state.projectReducer,
  );

  const { projectId } = useParams<{ projectId: string }>();
  const dispatch = useDispatch();

  const getSelectedCreators = (newCreators: CreatorForAO[]) => {
    const newSelectedCreators = newCreators.map((c) => c.wsTaskUuid);

    if (!audienceOverlap?.audienceOverlapData) {
      setSelectedCreators(newSelectedCreators);
      return;
    }

    const combinedUuids = [
      ...Object.values(audienceOverlap?.audienceOverlapData)
        .map((item) => item.wsTaskUuids)
        .flat(),
    ];

    const filteredUuids = combinedUuids.reduce((acc: string[], item) => {
      if (!acc.includes(item)) acc.push(item);
      return acc;
    }, []);

    const filteredCreators = newSelectedCreators.filter((c) => {
      if (!filteredUuids) return true;
      return filteredUuids.includes(c);
    });

    setSelectedCreators(filteredCreators);
  };

  const getData = async () => {
    try {
      const url = API_URLS.getCreatorsWithPublications.replace(
        ":wsProjectUuid:",
        projectId,
      );

      const {
        data: { content },
      } = await axios.get(url);

      const newCreators = content as CreatorForAO[];
      const sortedCreators =
        newCreators?.sort((a, b) => a.title.localeCompare(b.title)) || [];

      setAllCreators(sortedCreators);
      setFilteredCreators(sortedCreators);
      getSelectedCreators(sortedCreators);
    } catch (err) {
      showErrorToast();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const toggleSelectedAll = () => {
    if (selectedCreators.length === filteredCreators.length) {
      setSelectedCreators([]);
    } else {
      setSelectedCreators(filteredCreators.map((c) => c.wsTaskUuid));
    }
  };

  const toggleSelected = (uuid: string) => {
    if (selectedCreators.includes(uuid)) {
      setSelectedCreators(selectedCreators.filter((c) => c !== uuid));
    } else {
      setSelectedCreators([...selectedCreators, uuid]);
    }
  };

  const handleSelectChange = (nv: SelectOption) => {
    const newCreators = allCreators.filter((c) => {
      if (nv.value === "only-publishers") {
        return c.isPublisher;
      }

      return true;
    });
    const newSelectedCreators = selectedCreators.filter((c) =>
      newCreators.find((nc) => nc.wsTaskUuid === c),
    );

    setSelectedCreators(newSelectedCreators);
    setFilteredCreators(newCreators);
    setSelectValue(nv);
  };

  const handleConfirm = async () => {
    try {
      setAnalyzeLoading(true);
      const url = API_URLS.calculateAudienceOverlap.replace(
        ":projectUuid:",
        projectId,
      );

      await axios.post(url, {
        uuids: selectedCreators,
      });

      onClose();
      dispatch(getProjectAudienceOverlap(projectId, true));
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.errors) {
        const errorMessage = parseErrorMessages(error.response.data.errors)[0]
          .message;
        showErrorToast({
          id: errorMessage,
          defaultMessage: errorMessage,
        });
      } else {
        showErrorToast();
      }
    } finally {
      setAnalyzeLoading(false);
    }
  };

  const renderSharedIcon = (creator: CreatorForAO) => {
    if (!projectBasicData?.clientMode) return null;

    if (creator.shared) {
      return <PublisherIcon />;
    }

    return <NonPublisherIcon />;
  };

  const renderModalContent = () => {
    if (loading) return <MemoizedLoader />;

    return (
      <>
        <div className="select-creators-for-ao-modal__toolbar">
          <CustomCheckbox
            id="all"
            name="all"
            checked={selectedCreators.length === filteredCreators.length}
            onChange={toggleSelectedAll}
            data-qa="select-creators-all-checkbox"
          />
          <IDHFormattedMessage id="ws_select_all" defaultMessage="Select all" />
          <span className="select-creators-for-ao-modal__toolbar-counter">
            {filteredCreators.length}
          </span>
          <IDHFormattedMessage id="ws_creators" defaultMessage="creators" />

          <CustomSelect
            value={selectValue}
            options={selectOptions}
            onChange={handleSelectChange}
          />
        </div>

        <ModalScroller maxHeight={324}>
          {filteredCreators.map((creator) => (
            <div
              className="select-creators-for-ao-modal__creator"
              key={creator.wsTaskUuid}
            >
              <CustomCheckbox
                id={creator.wsTaskUuid}
                name={creator.title}
                checked={selectedCreators.includes(creator.wsTaskUuid)}
                onChange={() => toggleSelected(creator.wsTaskUuid)}
                data-qa={`select-creators-${creator.title}-checkbox`}
              />
              <Img
                className="select-creators-for-ao-modal__creator-avatar"
                src={creator?.cover?.thumbnails?.tiny}
                fallbackImageSrc={creatorPlaceholder}
              />
              <span className="select-creators-for-ao-modal__creator-data">
                <span className="select-creators-for-ao-modal__creator-data-info">
                  {creator.title}
                  {renderSharedIcon(creator)}
                </span>
                <SocialProfileIcons
                  data={creator.socialProfiles}
                  size="small"
                />
              </span>
            </div>
          ))}
        </ModalScroller>
      </>
    );
  };

  return (
    <Modal
      className="select-creators-for-ao-modal"
      overlayClassName="select-creators-for-ao-modal__overlay"
      onClose={onClose}
      displayCancelButton
      confirmButtonText={
        <IDHFormattedMessage id="ws_analyze" defaultMessage="Analyze" />
      }
      confirmButtonVariant="blue"
      confirmButtonDisabled={loading}
      closeButtonText={
        <IDHFormattedMessage id="ws_cancel" defaultMessage="Cancel" />
      }
      closeButtonVariant="white-with-light-grey-border"
      onConfirmClick={handleConfirm}
      confirmButtonLoading={analyzeLoading}
      outsideClick={OutsideClickModes.Allow}
    >
      <ModalTitle>
        <IDHFormattedMessage
          id="ws_select_creators_for_audience_overlap"
          defaultMessage="Select creators for Audience Overlap"
        />
      </ModalTitle>

      {renderModalContent()}
    </Modal>
  );
};
